// apps/web/src/pages/FavoritesPage.tsx
// 我的收藏 — Phase 4 实现

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listFavoritesApi } from "@api/userData";
import type { FavoriteDTO } from "@examfast/shared-types";

export default function FavoritesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bankId = searchParams.get("bankId") ?? undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ["favorites", bankId],
    queryFn: () => listFavoritesApi({ page_size: 50, bank_id: bankId }),
  });

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">加载失败，请重试</div>
    );
  }

  const items = data?.items ?? [];
  const groups = groupByQuestionType(items.map((item) => item.question));
  const firstQuestion = items[0]?.question;
  const startFavoritePractice = (questionId?: string) => {
    const target = questionId ? items.find((item) => item.question_id === questionId)?.question : firstQuestion;
    if (!target) return;
    navigate(`/banks/${target.bank_id}/practice/favorite${questionId ? `?questionId=${questionId}` : ""}`);
  };

  return (
    <div className="min-h-screen bg-white pb-8">
      <header className="bg-gradient-to-b from-violet-500 to-violet-400 text-white">
        <div className="relative flex h-14 items-center justify-center px-4">
          <button onClick={() => navigate(-1)} className="absolute left-3 flex h-10 w-10 items-center justify-center" aria-label="返回">
            <ChevronLeftIcon />
          </button>
          <h1 className="text-lg font-semibold">{bankId ? "题库收藏" : "我的收藏"}</h1>
          <div className="absolute right-3 flex h-9 items-center rounded-full bg-violet-700/30 px-3">
            <span className="text-lg leading-none">•••</span>
            <span className="mx-3 h-5 w-px bg-white/20" />
            <span className="h-5 w-5 rounded-full border-[5px] border-white" />
          </div>
        </div>
        <div className="px-6 pb-8 pt-8 text-center">
          <p className="text-xl font-semibold">收藏题目</p>
          <p className="mt-5 text-7xl font-light leading-none">
            {items.length}<span className="text-2xl">题</span>
          </p>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p>暂无收藏</p>
          <p className="text-sm mt-1">练习时收藏题目会显示在这里</p>
        </div>
      ) : (
        <div>
          <button onClick={() => startFavoritePractice()} className="flex w-full items-center justify-between border-b border-gray-100 px-5 py-6 text-left">
            <div>
              <p className="text-2xl font-semibold text-gray-900">全部收藏</p>
              <p className="mt-1 text-sm text-gray-400">点击开始收藏题练习</p>
            </div>
            <span className="text-xl font-semibold text-violet-500">{items.length}题 ›</span>
          </button>

          <section className="px-4 py-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">收藏分布</h2>
              <span className="text-base font-medium text-gray-400">收藏管理</span>
            </div>
            <div className="divide-y divide-gray-100">
              {groups.map((group) => (
                <button
                  key={group.type}
                  type="button"
                  onClick={() => startFavoritePractice(group.firstQuestionId)}
                  className="flex w-full items-center justify-between py-5 text-left"
                >
                  <span className="text-xl font-semibold text-gray-900">{group.label}</span>
                  <span className="text-lg font-semibold text-gray-400">{group.count}题 ›</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function getTypeLabel(type: string): string {
  const map: Record<string, string> = {
    single_choice: "单选",
    multiple_choice: "多选",
    true_false: "判断",
    fill_blank: "填空",
    essay: "简答",
  };
  return map[type] ?? type;
}

function groupByQuestionType(questions: FavoriteDTO["question"][]) {
  const map = new Map<string, { type: string; label: string; count: number; firstQuestionId: string }>();
  for (const question of questions) {
    const existing = map.get(question.type);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(question.type, {
        type: question.type,
        label: getTypeLabel(question.type),
        count: 1,
        firstQuestionId: question.id,
      });
    }
  }
  return [...map.values()];
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
